import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users,
  Linkedin,
  Mail,
  ShieldCheck,
  ScrollText,
  ArrowRight,
} from "lucide-react";
import api from "../lib/api";

/**
 * Public /organizers roster.
 *
 * The NCUA Form 9500 requires a documented organizing group of at least
 * seven people with named roles. This page publishes that roster so NCUA
 * reviewers, press, and supporters can verify the team.
 */
export default function Organizers() {
  const [rows, setRows] = useState([]);
  const [minRequired, setMinRequired] = useState(7);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/organizers")
      .then((r) => {
        if (cancelled) return;
        setRows(r.data?.rows || []);
        setMinRequired(r.data?.minimum_required || 7);
      })
      .catch(() => setRows([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const count = rows.length;
  const meetsMinimum = count >= minRequired;

  return (
    <div className="grain" data-testid="organizers-page">
      {/* Hero */}
      <section
        className="relative overflow-hidden border-b border-bdu-gold/10"
        data-testid="organizers-hero"
      >
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              "radial-gradient(1200px 500px at 20% 20%, rgba(212,175,55,0.25), transparent 60%)," +
              "radial-gradient(900px 500px at 80% 80%, rgba(46,164,79,0.14), transparent 60%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 py-24 lg:py-32 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8 animate-fade-up">
            <div className="label-eyebrow flex items-center gap-2">
              <ScrollText size={12} className="text-bdu-gold" /> NCUA Form 9500 · Public Record · Organizing Group
            </div>
            <h1 className="mt-5 font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.98] text-bdu-cream tracking-tight">
              The hands <span className="text-bdu-gold italic">building</span>
              <br />
              the vault.
            </h1>
            <p className="mt-7 text-lg text-bdu-cream/75 max-w-2xl leading-relaxed">
              Under NCUA Chartering Manual Chapter 2, a federal credit union
              charter requires a documented organizing group of at least seven
              individuals committed to establishing and operating the
              institution. This is ours — on the record, publicly accountable.
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <div
              className={`relative border p-6 w-full max-w-sm ${
                meetsMinimum ? "border-bdu-gold/60 bg-bdu-gold/5" : "border-bdu-red/40 bg-bdu-red/5"
              }`}
              data-testid="organizers-count-card"
            >
              <div className="text-[10px] uppercase tracking-[0.26em] text-bdu-cream/60">
                Organizing Group
              </div>
              <div
                className={`mt-3 font-serif text-6xl leading-none ${
                  meetsMinimum ? "text-bdu-gold" : "text-bdu-red"
                }`}
              >
                {count}
              </div>
              <div className="mt-2 text-sm text-bdu-cream/70">
                of <span className="text-bdu-cream font-semibold">{minRequired}</span>{" "}
                NCUA-required minimum
              </div>
              <div className="mt-5 flex items-center gap-2 text-[11px]">
                <ShieldCheck
                  size={13}
                  className={meetsMinimum ? "text-bdu-gold" : "text-bdu-red"}
                />
                <span className={meetsMinimum ? "text-bdu-gold" : "text-bdu-red"}>
                  {meetsMinimum ? "Charter minimum met" : "Below minimum — recruiting"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Roster grid */}
      <section className="relative" data-testid="organizers-roster-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20">
          {loading ? (
            <div className="text-center py-16 text-bdu-cream/50">Loading roster…</div>
          ) : count === 0 ? (
            <div className="border border-bdu-gold/15 bg-black/40 py-16 text-center">
              <div className="text-bdu-cream/50">No organizers published yet.</div>
            </div>
          ) : (
            <div
              className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8"
              data-testid="organizers-grid"
            >
              {rows.map((o, i) => (
                <OrganizerCard key={o.id} organizer={o} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Governance / mandate */}
      <section
        className="relative border-y border-bdu-gold/10"
        style={{
          background:
            "radial-gradient(900px 400px at 50% 50%, rgba(212,175,55,0.08) 0%, rgba(0,0,0,0) 60%)",
        }}
      >
        <div className="max-w-4xl mx-auto px-6 sm:px-10 py-20">
          <div className="label-eyebrow">The Mandate</div>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl text-bdu-cream leading-tight max-w-3xl">
            What this group commits to.
          </h2>
          <ul className="mt-8 space-y-5 text-bdu-cream/75 text-[15px] leading-relaxed">
            <MandateRow
              label="Submit the Form 9500"
              detail="Prepare and submit the Federal Credit Union Charter Application to the NCUA Office of Credit Union Resources & Expansion with every required attachment."
            />
            <MandateRow
              label="Steward the pledges"
              detail="Hold every $100 pledge in a segregated escrow account at Citizen Trust Bank — refundable in full if the charter is not granted."
            />
            <MandateRow
              label="Serve the WDLC"
              detail="Build an institution purpose-built for the Well-Defined Local Community of Fulton County, DeKalb County, and the City of College Park, Georgia."
            />
            <MandateRow
              label="Operate with transparency"
              detail="Publish the organizing roster, Field-of-Membership study, charter supporter counts, and NCUA correspondence milestones on this public site."
            />
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 text-center">
        <div className="max-w-3xl mx-auto px-6 sm:px-10">
          <h2 className="font-serif text-3xl sm:text-4xl text-bdu-cream leading-tight">
            Help this group reach the charter.
          </h2>
          <p className="mt-5 text-bdu-cream/65 max-w-xl mx-auto leading-relaxed">
            Add your name to the roll of charter supporters. The organizing
            group signs the application — the community proves the demand.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-3">
            <Link
              to="/credit-union"
              className="btn-gold px-7 py-3.5"
              data-testid="organizers-cta-pledge"
            >
              Pledge Your Support <ArrowRight size={16} />
            </Link>
            <Link
              to="/field-of-membership"
              className="btn-ghost px-7 py-3.5"
              data-testid="organizers-cta-fom"
            >
              See the Field of Membership
            </Link>
          </div>
          <p className="mt-8 text-[11px] text-bdu-cream/40 italic max-w-xl mx-auto leading-relaxed">
            Proposed Credit Union · pending NCUA charter approval · pledges are
            non-binding and not deposits.
          </p>
        </div>
      </section>
    </div>
  );
}

function OrganizerCard({ organizer, index }) {
  const { name, role, bio, photo_url, linkedin_url, email } = organizer;
  const initials = (name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("");

  return (
    <article
      data-testid={`organizer-card-${organizer.id}`}
      className="relative border border-bdu-gold/20 bg-black/50 p-6 sm:p-7 flex flex-col gap-5 animate-fade-up transition-colors hover:border-bdu-gold/50"
      style={{
        animationDelay: `${index * 50}ms`,
        boxShadow: "inset 0 1px 0 rgba(212,175,55,0.06)",
      }}
    >
      <div className="flex items-center gap-4">
        {photo_url ? (
          <img
            src={photo_url}
            alt={name}
            className="w-20 h-20 rounded-full object-cover border border-bdu-gold/40"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center border border-bdu-gold/40 bg-bdu-gold/10 font-serif text-2xl text-bdu-gold"
            aria-hidden
          >
            {initials || <Users size={24} />}
          </div>
        )}
        <div className="min-w-0">
          <h3 className="font-serif text-xl text-bdu-cream leading-tight">
            {name}
          </h3>
          <div className="mt-1 text-[11px] uppercase tracking-[0.22em] text-bdu-gold">
            {role}
          </div>
        </div>
      </div>

      {bio ? (
        <p className="text-sm text-bdu-cream/70 leading-relaxed line-clamp-6">
          {bio}
        </p>
      ) : null}

      {(linkedin_url || email) && (
        <div className="mt-auto pt-4 border-t border-bdu-gold/10 flex items-center gap-4 text-xs text-bdu-cream/55">
          {linkedin_url && (
            <a
              href={linkedin_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 hover:text-bdu-gold transition-colors"
              data-testid={`organizer-linkedin-${organizer.id}`}
            >
              <Linkedin size={13} /> LinkedIn
            </a>
          )}
          {email && (
            <a
              href={`mailto:${email}`}
              className="inline-flex items-center gap-1.5 hover:text-bdu-gold transition-colors"
              data-testid={`organizer-email-${organizer.id}`}
            >
              <Mail size={13} /> Contact
            </a>
          )}
        </div>
      )}
    </article>
  );
}

function MandateRow({ label, detail }) {
  return (
    <li className="flex items-start gap-4">
      <div className="flex-shrink-0 mt-1.5 w-2 h-2 rounded-full bg-bdu-gold" />
      <div>
        <div className="font-serif text-xl text-bdu-cream leading-tight">{label}</div>
        <div className="mt-1 text-bdu-cream/70">{detail}</div>
      </div>
    </li>
  );
}
